import styled from '@emotion/styled';

/**
 * The full-screen backdrop. It is owned by the EPOS root rather than by the
 * Welcome screen, because it stays put for the whole flow - the menu sheet
 * flies up over it and leaves the top of the screen showing through. The
 * photo comes from backoffice (eposCloudConfig.backgroundMediaSrcUrl) via
 * `backgroundUrl`; without one the stage is plain black and shows
 * StageNoBackground.
 */
export const Stage = styled.div<{ backgroundUrl?: string | null }>`
  position: relative;
  /* --eposZoom (styles/index.css) shrinks the whole flow on narrow screens.
     Viewport units are scaled by zoom too, so divide them back out to keep
     the stage filling the screen. */
  zoom: var(--eposZoom);
  width: calc(100vw / var(--eposZoom));
  height: calc(100vh / var(--eposZoom));
  overflow: hidden;
  font-family: var(--eposFontFamily);
  background-color: #000;
  background-image: ${(props) => (props.backgroundUrl ? `url(${props.backgroundUrl})` : 'none')};
  background-position: center;
  background-size: cover;
`;

/**
 * Full-stage looping video, used instead of `backgroundUrl` when backoffice's
 * background media is a video. Purely decorative - it never takes touches and
 * every native control overlay (incl. iOS's big start-play button) is hidden.
 */
export const StageVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;

  &::-webkit-media-controls,
  &::-webkit-media-controls-start-playback-button,
  &::-webkit-media-controls-panel {
    display: none !important;
    -webkit-appearance: none;
  }
`;

/** Shown centered on the stage when backoffice has no background photo configured. */
export const StageNoBackground = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 18px;
  pointer-events: none;
`;

/** Darkens the photo so white text and the sheet edge stay readable on it. */
export const StageScrim = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.55) 0%,
    rgba(0, 0, 0, 0.15) 32%,
    rgba(0, 0, 0, 0.3) 60%,
    rgba(0, 0, 0, 0.8) 100%
  );
`;

/** A layer filling the stage - used for the welcome content and the menu sheet. */
export const StageLayer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
`;
